if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector("gallery-dots"),
        };
        this.mql = window.matchMedia("(min-width: 750px)");
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500)
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
          });
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();

        // Hooks for the 'thumbnail_below' layout: dots + click-and-drag thumbnails
        if (this.dataset.galleryLayout === "thumbnail_below") {
          this.initGalleryDots();
          this.initThumbnailDrag();
        }
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
        this.setActiveDot(mediaId);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia?.classList?.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.setActiveDot(mediaId);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }

      // ---- Dots (pagination) ----

      initGalleryDots() {
        if (!this.elements.dots) return;
        this.elements.dots
          .querySelectorAll(".gallery-dots__dot")
          .forEach((dot) => {
            dot.addEventListener("click", () => {
              const target = dot.dataset.target;
              if (target) this.setActiveMedia(target, false);
            });
          });
      }

      setActiveDot(mediaId) {
        if (!this.elements.dots) return;
        this.elements.dots
          .querySelectorAll(".gallery-dots__dot")
          .forEach((dot) => {
            if (dot.dataset.target === mediaId) {
              dot.setAttribute("aria-current", "true");
            } else {
              dot.removeAttribute("aria-current");
            }
          });
      }

      // ---- Click-and-drag for the thumbnail strip ----

      initThumbnailDrag() {
        const slider = this.elements.thumbnails.querySelector(
          '[id^="Slider-Thumbnails"]'
        );
        if (!slider) return;

        let isDown = false;
        let startX = 0;
        let startScrollLeft = 0;
        let moved = false;
        const DRAG_THRESHOLD = 5; // px before we treat it as a drag

        const onPointerDown = (e) => {
          // Only react to the primary mouse button (ignore touch — native scroll handles that)
          if (e.pointerType === "touch") return;
          isDown = true;
          moved = false;
          startX = e.clientX;
          startScrollLeft = slider.scrollLeft;
          slider.classList.add("is-dragging");
        };

        const onPointerMove = (e) => {
          if (!isDown) return;
          const dx = e.clientX - startX;
          if (Math.abs(dx) > DRAG_THRESHOLD) moved = true;
          slider.scrollLeft = startScrollLeft - dx;
        };

        const endDrag = () => {
          if (!isDown) return;
          isDown = false;
          slider.classList.remove("is-dragging");
          // Suppress the click that follows a drag, otherwise the thumbnail
          // under the pointer will fire after the drag ends.
          if (moved) {
            const suppress = (ev) => {
              ev.stopPropagation();
              ev.preventDefault();
              slider.removeEventListener("click", suppress, true);
            };
            slider.addEventListener("click", suppress, true);
          }
        };

        slider.addEventListener("pointerdown", onPointerDown);
        slider.addEventListener("pointermove", onPointerMove);
        slider.addEventListener("pointerup", endDrag);
        slider.addEventListener("pointercancel", endDrag);
        slider.addEventListener("pointerleave", endDrag);
      }
    }
  );
}

// Custom element wrapper for the dots container.
// MediaGallery handles the click logic; this element just provides a hook
// so styles and queries can target it cleanly.
if (!customElements.get("gallery-dots")) {
  customElements.define(
    "gallery-dots",
    class GalleryDots extends HTMLElement {
      constructor() {
        super();
      }
    }
  );
}
